// Travel + Discover planning API client (web). Drives the backend AI breakdown
// flow that the mobile app uses — the "dual breakdown" rule: Shopping uses the
// client-side AI; Travel/Discover use the BACKEND breakdown endpoint. This
// service only ever calls the backend, so that rule is preserved on web.
//
// Endpoints (see backend/api/urls.py + views_breakdown.py + views.py):
//   POST /api/parse-task/                  create the base task
//   POST /api/update-task-status/          set category_metadata (+ get replan)
//   GET  /api/tasks/                       list tasks (incl. category_metadata + subtasks)
//   POST /api/tasks/<id>/breakdown/        generate / regenerate the itinerary
//   POST /api/subtasks/<id>/booking/       confirm a booking (+ replan)
//   PUT  /api/subtask/<id>/                toggle complete / edit
//   DELETE /api/subtask/<id>/delete/       remove a subtask

import axios from 'axios';
import API_BASE_URL from '../config';

const BASE_URL = `${API_BASE_URL}/api`;

const authHeaders = () => {
  const accessToken = localStorage.getItem('accessToken');
  if (!accessToken) { window.location.href = '/login'; throw new Error('No access token'); }
  return { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' };
};
const on401 = (err) => {
  if (err.response?.status === 401) {
    localStorage.removeItem('accessToken');
    window.location.href = '/login';
  }
};

// Convert an ISO date ("YYYY-MM-DD") to the backend's y/m/week/day bucket.
export const dateParams = (iso) => {
  const d = iso ? new Date(`${iso}T00:00:00`) : new Date();
  return {
    year: d.getFullYear(),
    month: d.getMonth() + 1,
    week: Math.ceil(d.getDate() / 7),
    day: d.toLocaleDateString('en-US', { weekday: 'long' }),
    full_date: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`,
  };
};

// Create a Travel/Discover task, then attach its category_metadata.
// type: 'Travel' | 'Discover'; returns the created task object (with metadata).
export const createPlanTask = async ({ type, title, metadata, dateISO }) => {
  const dp = dateParams(dateISO);
  let created;
  try {
    const res = await axios.post(`${BASE_URL}/parse-task/`, {
      task_text: title,
      category: type,
      year: dp.year, month: dp.month, week: dp.week, day: dp.day, full_date: dp.full_date,
    }, { headers: authHeaders() });
    created = res.data.parsed_task;
  } catch (err) { on401(err); throw err; }

  // Attach metadata via the task-update route (sets category_metadata).
  try {
    const res = await axios.post(`${BASE_URL}/update-task-status/`, {
      id: created.id,
      category_metadata: metadata,
    }, { headers: authHeaders() });
    return { ...created, ...res.data.task, subtasks: [] };
  } catch (err) { on401(err); throw err; }
};

// Update a plan task's metadata (Edit). Returns { task, replan }.
export const updatePlanMeta = async (taskId, metadata) => {
  try {
    const res = await axios.post(`${BASE_URL}/update-task-status/`, {
      id: taskId, category_metadata: metadata,
    }, { headers: authHeaders() });
    return res.data;
  } catch (err) { on401(err); throw err; }
};

// List Travel + Discover tasks for a given ISO date (with subtasks + metadata).
export const fetchPlans = async (dateISO) => {
  const dp = dateParams(dateISO);
  try {
    const url = `${BASE_URL}/tasks/?year=${dp.year}&month=${dp.month}&week=${dp.week}&day=${dp.day}`;
    const res = await axios.get(url, { headers: authHeaders() });
    const groups = res.data.tasks || {};
    const pick = (k) => groups[k] || groups[k?.toLowerCase?.()] || [];
    return [...pick('Travel'), ...pick('Discover')];
  } catch (err) { on401(err); throw err; }
};

// Generate / regenerate the itinerary. Returns { task_id, cause, brief, subtasks }.
export const generateBreakdown = async (taskId, { cause = 'first_breakdown', force = false } = {}) => {
  try {
    const res = await axios.post(`${BASE_URL}/tasks/${taskId}/breakdown/`,
      { cause, force_regenerate: force }, { headers: authHeaders() });
    return res.data;
  } catch (err) { on401(err); throw err; }
};

// Confirm a booking on a subtask. fields: { confirmation_code, provider, notes, total_cost, currency }.
// Returns { booking, subtask_id, replan }.
export const recordBooking = async (subtaskId, fields) => {
  try {
    const res = await axios.post(`${BASE_URL}/subtasks/${subtaskId}/booking/`, fields, { headers: authHeaders() });
    return res.data;
  } catch (err) { on401(err); throw err; }
};

// Toggle/edit a subtask. Returns { subtask, replan }.
export const updateSubtask = async (subtaskId, patch) => {
  try {
    const res = await axios.put(`${BASE_URL}/subtask/${subtaskId}/`, patch, { headers: authHeaders() });
    return res.data;
  } catch (err) { on401(err); throw err; }
};

// Delete a subtask. Returns { replan }.
export const deleteSubtask = async (subtaskId) => {
  try {
    const res = await axios.delete(`${BASE_URL}/subtask/${subtaskId}/delete/`, { headers: authHeaders() });
    return res.data;
  } catch (err) { on401(err); throw err; }
};
